import { UnknownAction } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import * as uuid from 'uuid';
import { SinatraState } from '@rsos/sinatra';
import { notifySuccess } from '@rsos/utils/toastUtils';
import { KeywordTier } from './KeywordTier';
import {
  fetchKeywordSets,
  keywordSetsSelector,
  KeywordSetsState,
  updateKeywordSets,
} from './keywordSetsSlice';

export type Speaker = 'operator' | 'caller' | '';

export type KeywordSet = {
  name: string;
  id: string;
  keywords: string[];
  speaker: Speaker;
  tier_id: KeywordTier['id'];
  builtin?: boolean;
};

type KeywordSetsDispatch = ThunkDispatch<
  { keywordSets: KeywordSetsState; sinatra: SinatraState },
  string,
  UnknownAction
>;

export const useKeywordSets = () => {
  const dispatch: KeywordSetsDispatch = useDispatch();
  const keywordSetState = useSelector(keywordSetsSelector);

  useEffect(() => {
    if (
      !keywordSetState.keywordSets &&
      !keywordSetState.loading &&
      !keywordSetState.loadingError
    ) {
      dispatch(fetchKeywordSets());
    }
  }, [
    dispatch,
    keywordSetState.keywordSets,
    keywordSetState.loading,
    keywordSetState.loadingError,
  ]);

  const sets = [...(keywordSetState.keywordSets || [])];
  sets.sort((a, b) => a.name.localeCompare(b.name));

  const keywordSetMap = Object.fromEntries(sets.map(s => [s.id, s]));

  return { keywordSets: sets, keywordSetMap };
};

export const useKeywordSetActions = (set?: KeywordSet) => {
  const [, setSearchParams] = useSearchParams();
  const dispatch: KeywordSetsDispatch = useDispatch();
  const { keywordSets: allSets } = useKeywordSets();
  const otherSets = allSets.filter(
    (otherSet: KeywordSet) => otherSet.id != set?.id,
  );

  return {
    create(newSet: KeywordSet) {
      return dispatch(
        updateKeywordSets([...otherSets, { ...newSet, builtin: false }]),
      )
        .unwrap()
        .then(() => notifySuccess(`${newSet.name} has been created.`));
    },

    duplicate() {
      if (!set) throw Error('No Keyword set available to duplicate');
      const newSet = {
        ...set,
        name: `${set.name} (copy)`,
        id: uuid.v4(),
      };
      return dispatch(updateKeywordSets([...allSets, newSet]))
        .unwrap()
        .then(() => setSearchParams({ [newSet.id]: 'open' }));
    },

    update(newSet: KeywordSet) {
      if (!set) throw Error('No Keyword set available to update');
      return dispatch(
        updateKeywordSets([
          ...otherSets,
          { ...newSet, id: set?.id, builtin: false },
        ]),
      )
        .unwrap()
        .then(() => notifySuccess(`"${newSet.name}" has been saved.`));
    },

    destroy() {
      return dispatch(updateKeywordSets(otherSets)).unwrap();
    },

    reset() {
      // THIS RESETS ALL KEYWORDS TO DEFAULTS.
      // Remove when removing `reset_keyword_defaults`
      return dispatch(
        updateKeywordSets(null as unknown as KeywordSet[]),
      ).unwrap();
    },
  };
};

export const keywordSpeakerDisplayText = (speaker: Speaker) => {
  return { operator: 'Dispatcher', caller: 'Caller', '': 'Anyone' }[speaker];
};
